import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Link, Skeleton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { colors } from "../../../../../theme/palette";
import { VerificationIcon } from "../../../../../assets/icons/MUI/NetworkIcons";
import { UserInfo } from "../../../../../types";
import { useProfileData } from "../../../../../hooks/useProfileData";
import { useDataFormatter } from "../../../../../hooks/useDataFormatter";

const PreviewUserCard = ({
  user,
  isLast,
  handleRemoveUser,
}: {
  user: UserInfo;
  isLast: boolean;
  handleRemoveUser: (user: UserInfo | null) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    fullName,
    firstTwoChr,
    isVerified,
    media,
    role,
    profileLink,
    mutualFollowersCount,
    playerPositionShort,
    friendsCount,
    id,
    entityId,
  } = useDataFormatter(user);

  const { data: ownProfileData } = useProfileData();
  const { entityId: entityIdFromCache } = ownProfileData;

  const isOwnProfile = entityId == entityIdFromCache && entityIdFromCache != null;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        columnGap: "10px",
        paddingBottom: isLast ? "0" : "16px",
        marginBottom: isLast ? "0" : "16px",
        borderBottom: isLast ? "unset" : "1px solid #EAECF0",
      }}
    >
      <Box
        sx={{
          maxWidth: "calc(100% - 74px)",
          display: "flex",
          justifyContent: "flex-start",
          columnGap: "10px",
        }}
      >
        <Avatar
          sx={{
            width: "49px",
            height: "49px",
            position: "relative",
            overflow: "visible",
          }}
          src={media}
        >
          {firstTwoChr}
          {isVerified && (
            <VerificationIcon
              sx={{
                position: "absolute",
                bottom: "-6px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: "10",
              }}
            />
          )}
        </Avatar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxWidth: "70%",
          }}
        >
          <Link
            sx={{
              textDecoration: "none",
              color: colors.dark1000,
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: "normal",
              maxWidth: "95%",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={() => navigate(profileLink)}
          >
            {fullName}
          </Link>
          <Typography
            component="span"
            variant="body12Regular"
            sx={{
              color: colors.Dark1000,
            }}
          >
            {role}
            {playerPositionShort ? ` . (${playerPositionShort})` : ""}
          </Typography>

          <Typography
            component="span"
            variant="body12Regular"
            sx={{
              color: colors.Dark600,
            }}
          >
            {friendsCount ? `${friendsCount} ${t("components.networkController.userCard.friends")}` : ""}
          </Typography>

          {isOwnProfile ? (
            <></>
          ) : (
            <Typography
              component="span"
              variant="body12Regular"
              sx={{
                color: colors.Dark600,
              }}
            >
              {`${mutualFollowersCount} ${t("components.networkController.userCard.commonFollowers")}`}
            </Typography>
          )}
        </Box>
      </Box>
      <Button
        variant="text-sm"
        onClick={() => handleRemoveUser(user)}
        sx={{
          color: colors.Red200,
          background: "transparent",
          padding: 0,
          borderRadius: 0,
          height: "fit-content",
          "&:hover": {
            color: `${colors.Red200} !important`,
            background: "transparent !important",
          },
        }}
      >
        {t("pages.entity.groups.remove")}
      </Button>
    </Box>
  );
};

export default PreviewUserCard;

export const PreviewUserCardLoader = ({ isLast }: { isLast: boolean }) => {
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        columnGap: "10px",
        paddingBottom: isLast ? "0" : "16px",
        marginBottom: isLast ? "0" : "16px",
        borderBottom: isLast ? "unset" : "1px solid #EAECF0",
      }}
    >
      <Box
        sx={{
          maxWidth: "calc(100% - 74px)",
          display: "flex",
          justifyContent: "flex-start",
          columnGap: "10px",
        }}
      >
        <Skeleton variant="circular" width={49} height={49} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Skeleton variant="text" width="120px" height="20px" />
          <Skeleton variant="text" width="60px" height="16px" />
          <Skeleton variant="text" width="40px" height="16px" />
        </Box>
      </Box>
      <Button
        variant="text-sm"
        sx={{
          color: colors.Red200,
          background: "transparent",
          padding: 0,
          borderRadius: 0,
          height: "fit-content",
          "&:hover": {
            color: `${colors.Red200} !important`,
            background: "transparent !important",
          },
        }}
        disabled
      >
        <Skeleton variant="text" width="50px" height="20px" />
      </Button>
    </Box>
  );
};
